/**
 * JSON-LD schema builders (BOO AI-SEO explainer pack).
 *
 * AI answer engines (AI Overviews, ChatGPT search, Perplexity) preferentially
 * cite pages that declare what they are in machine-readable form. These helpers
 * are the single source of the schema shapes so the SPA (react-helmet-async)
 * and the static HTML entries can be asserted equal in tests — a hand-edited
 * <script type="application/ld+json"> block that drifts from the page content
 * is worse than no block at all.
 *
 * Everything here is pure data: no React, no DOM. Consumers stringify.
 */

export const SITE_URL = 'https://www.bookeper.pl';

export const PUBLISHER = {
  '@type': 'Organization',
  name: 'Bookeper AI',
  url: SITE_URL,
} as const;

export interface FaqItem {
  /** Question, phrased the way a reader would actually type it. */
  q: string;
  /** Answer in plain declarative sentences — this is the quotable unit. */
  a: string;
}

export interface ArticleSchemaInput {
  headline: string;
  description: string;
  /** Canonical absolute URL of the page. */
  url: string;
  /** ISO date (YYYY-MM-DD). */
  datePublished: string;
  /** ISO date (YYYY-MM-DD). Defaults to datePublished. */
  dateModified?: string;
  /** BCP-47 language tag of the article body. */
  inLanguage?: string;
}

/** schema.org/Article — tells engines this is editorial content, not a product page. */
export function articleSchema(input: ArticleSchemaInput): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: input.headline,
    description: input.description,
    inLanguage: input.inLanguage ?? 'pl-PL',
    datePublished: input.datePublished,
    dateModified: input.dateModified ?? input.datePublished,
    mainEntityOfPage: { '@type': 'WebPage', '@id': input.url },
    url: input.url,
    author: PUBLISHER,
    publisher: PUBLISHER,
  };
}

/** schema.org/FAQPage — the block AI engines lift verbatim into answers. */
export function faqSchema(items: readonly FaqItem[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };
}

export interface BreadcrumbItem {
  name: string;
  /** Absolute URL. */
  url: string;
}

/** schema.org/BreadcrumbList — places the guide inside the site hierarchy. */
export function breadcrumbSchema(items: readonly BreadcrumbItem[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export interface WebPageSoftwareSchemaInput {
  name: string;
  /** Canonical absolute URL of the page. */
  url: string;
  /** ISO date (YYYY-MM-DD) of the last copy review. */
  dateModified: string;
  /** What the product costs, in the words the page uses. */
  offerDescription: string;
}

/**
 * schema.org/WebPage about a SoftwareApplication (/ksiegowosc-ai). It declares the offer exactly
 * as the page states it and nothing more: no AggregateRating, no Review, no promotion dates.
 */
export function webPageSoftwareSchema(input: WebPageSoftwareSchemaInput): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: input.name,
    url: input.url,
    inLanguage: 'pl-PL',
    dateModified: input.dateModified,
    isPartOf: { '@type': 'WebSite', name: 'Bookeper', url: `${SITE_URL}/` },
    about: {
      '@type': 'SoftwareApplication',
      name: 'Bookeper',
      applicationCategory: 'FinanceApplication',
      operatingSystem: 'Web',
      url: 'https://app.bookeper.pl',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'PLN',
        description: input.offerDescription,
      },
    },
  };
}
